import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Copy, Download, Users } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnnualCheckCard({ authHeader }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/mlo/annual-check`, { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [authHeader]);

  if (!data) return null;
  const { season, totals, partners, share_url: shareUrl, assets } = data;

  const copy = async (value, what) => {
    const ok = await copyText(value);
    toast[ok ? "success" : "error"](ok ? `${what} copied` : "Copy it manually");
  };

  const statusText = season.open
    ? `Open · closes ${season.closes_on} · ${season.days_left} days left`
    : season.phase === "before" && season.enabled
      ? `Opens ${season.opens_on}`
      : "Season closed — anniversary checks continue";

  return (
    <div
      className="bg-white border border-linen-border rounded-3xl shadow-soft overflow-hidden"
      data-testid="mlo-annual-check-card"
    >
      <div className="bg-navy text-white px-6 sm:px-8 py-6">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-lime">
          <CalendarCheck size={14} /> {season.label}
        </div>
        <h3 className="mt-2 font-heading font-medium text-2xl">Your partners' yearly check-up push</h3>
        <p className="mt-1.5 text-sm text-white/70 max-w-2xl leading-relaxed">
          {statusText}. Every check your partners run is a homeowner asking what their mortgage looks
          like today — and the handoff comes to you.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            onClick={() => copy(shareUrl, "Campaign link")}
            data-testid="mlo-annual-copy-link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-lime text-navy-deep hover:brightness-95"
          >
            <Copy size={12} /> Copy my campaign link
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="mlo-annual-open-page"
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-white/10 text-white hover:bg-white/20"
          >
            Open the campaign page
          </a>
        </div>
      </div>

      <div className="px-6 sm:px-8 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Partners running it", value: `${totals.running}/${totals.partners}`, testid: "mlo-annual-running" },
            { label: "Homeowners checked", value: totals.checked, testid: "mlo-annual-checked" },
            { label: "Still to go", value: totals.remaining, testid: "mlo-annual-remaining" },
            { label: "Names on lists", value: totals.contacts, testid: "mlo-annual-contacts" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-linen/60 border border-linen-border p-4" data-testid={s.testid}>
              <div className="text-2xl font-heading font-medium text-navy leading-none">{s.value}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-widest text-ink-400 font-bold inline-flex items-center gap-1.5">
            <Users size={12} /> By partner
          </p>
          {partners.length === 0 ? (
            <p className="mt-2 text-sm text-ink-500" data-testid="mlo-annual-no-partners">
              No partners assigned to you yet.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-linen-border border-y border-linen-border" data-testid="mlo-annual-partners">
              {partners.map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-navy truncate">{p.name}</div>
                    <div className="text-[11px] text-ink-500">
                      {p.total === 0 ? "No names on their list yet" : `${p.checked} of ${p.total} checked`}
                    </div>
                  </div>
                  <div className="w-28 h-1.5 rounded-full bg-linen overflow-hidden shrink-0">
                    <div className="h-full rounded-full bg-teal" style={{ width: `${p.pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-navy w-10 text-right">{p.pct}%</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <ul className="mt-6 flex flex-wrap gap-2" data-testid="mlo-annual-assets">
          {assets.map((a) => (
            <li key={a.url}>
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-linen-border bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-teal transition-colors"
              >
                <Download size={12} className="text-teal" /> {a.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
